import React, { useState, useEffect, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { formatCurrency, formatDate, getStatusBadgeStyle } from 'utils/lmmsHelpers';
import { regroupService } from 'api/services/regroupService';
import { SyutSelect } from 'components/ui';
import { useCustomers, useGoldRates, useSystemConfig } from 'hooks/api';

// ============================================================================
// Types
// ============================================================================

/** A GL of the existing lot with the user's Close/Retain choice */
export interface RegroupClosingGlRow {
  mappedGlId: string;
  glNumber: string;
  netWeight: number;
  allocationRatio: number;
  close: boolean;                 // true = close, false = retain (carried to new lot)
  outstandingPrincipal: string;
  outstandingInterest: string;
  tokenLossCharge: string;
}

/** A freshly pledged GL for the new lot */
export interface RegroupNewGlRow {
  customerId: string;             // customer UUID
  glNumber: string;
  netWeight: string;
  loanAmount: string;
  documentationCharge: string;
  loanDate: string;
}

export interface RegroupFormData {
  mappingId: string;
  mappingRef: string;
  customerName: string;
  customerId: string;
  addonCustomerId: string;        // UUID for the new Add-On customer
  loanAccountNumber: string;      // old Add-On account
  addonLoanType: string;
  newAddonLoanAccountNumber: string;
  newAddonLoanDate: string;
  addonOutstandingPrincipal: string;
  addonOutstandingInterest: string;
  requestedAmount: string;
  closingGls: RegroupClosingGlRow[];
  newGls: RegroupNewGlRow[];
  // Cut-off context (from search)
  addonLoanDate: string;
  cutoffDate: string;
  cutoffEligible: boolean;
  cutoffReason: string;
}

/** Regroup record for the view modal / list */
export interface RegroupRecord {
  id: string;
  regroupRef?: string;
  closingTransactionId: string;
  newRequestId: string;
  customerName?: string;
  customerId?: string;
  mappingReference?: string;
  glsClosed?: number;
  glsRetained?: number;
  newAddonAmount?: number;
  netCashFlow: number;
  statusCode?: string;
  statusName?: string;
  submittedByName?: string;
  submittedAt?: string;
  createdByName?: string;
  createdAt: string;
  rowVersion?: number;
  businessUnitName?: string;
}

// ============================================================================
// Shared styles
// ============================================================================

const sectionHeaderStyle: React.CSSProperties = {
  fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.75rem',
  color: 'var(--primary)', borderBottom: '2px solid var(--primary)', paddingBottom: '0.5rem',
};
const thBase: React.CSSProperties = { padding: '0.375rem', textAlign: 'left', fontWeight: 600, fontSize: '0.72rem', borderBottom: '2px solid var(--border-color)' };
const thRight: React.CSSProperties = { ...thBase, textAlign: 'right', whiteSpace: 'nowrap' };
const cellInput: React.CSSProperties = { width: '90px', textAlign: 'right', fontSize: '0.72rem', padding: '0.25rem 0.375rem' };

// ============================================================================
// Local recalculation — mirrors backend RegroupCalculator / Worked Example 0.4
// ============================================================================

export function computeRegroup(data: RegroupFormData, ratePerGram: number, roundingUnit: number) {
  const addonP = parseFloat(data.addonOutstandingPrincipal) || 0;
  const addonI = parseFloat(data.addonOutstandingInterest) || 0;
  const requested = parseFloat(data.requestedAmount) || 0;

  const closed = data.closingGls.filter((g) => g.close);
  const retained = data.closingGls.filter((g) => !g.close);

  const closedRatio = closed.reduce((s, g) => s + g.allocationRatio, 0);
  const closedPrincipalPortion = Math.round(addonP * closedRatio);
  const carried = addonP - closedPrincipalPortion;

  const newWeight = data.newGls.reduce((s, g) => s + (parseFloat(g.netWeight) || 0), 0);
  const newGlLoan = data.newGls.reduce((s, g) => s + (parseFloat(g.loanAmount) || 0), 0);
  const newGlGross = Math.round(newWeight * ratePerGram);
  const newGlEligibility = newGlGross - newGlLoan;

  const eligible = newGlEligibility + carried;
  const maximumPermissible = eligible > 0 && roundingUnit > 0 ? Math.ceil(eligible / roundingUnit) * roundingUnit : 0;

  const tokenLossTotal = closed.reduce((s, g) => s + (parseFloat(g.tokenLossCharge) || 0), 0);
  const closedGlSettlement = closed.reduce((s, g) => s + (parseFloat(g.outstandingPrincipal) || 0) + (parseFloat(g.outstandingInterest) || 0) + (parseFloat(g.tokenLossCharge) || 0), 0);
  const addonSettlement = addonP + addonI;
  const closingLegTotal = closedGlSettlement + addonSettlement;

  const docTotal = data.newGls.reduce((s, g) => s + (parseFloat(g.documentationCharge) || 0), 0);
  const newGlDisbursementTotal = newGlLoan - docTotal;
  const newLotAmount = requested + newGlDisbursementTotal;
  const netCashFlow = newLotAmount - closingLegTotal;

  const totalNewLotWeight = newWeight + retained.reduce((s, g) => s + g.netWeight, 0);

  const docInvalid = data.newGls.some((g) => (parseFloat(g.documentationCharge) || 0) > 0 && (parseFloat(g.documentationCharge) || 0) >= (parseFloat(g.loanAmount) || 0));

  return {
    closedRatio, closedPrincipalPortion, carried,
    newWeight, newGlLoan, newGlGross, newGlEligibility,
    eligible, maximumPermissible,
    tokenLossTotal, closedGlSettlement, addonSettlement, closingLegTotal,
    newGlDisbursementTotal, newLotAmount, netCashFlow,
    totalNewLotWeight, docInvalid, requested,
    eligibilityBlocked: eligible <= 0,
    exceedsMaximum: requested > maximumPermissible,
  };
}

// ============================================================================
// Form Modal
// ============================================================================

interface RegroupFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  formData: RegroupFormData;
  onFormDataChange: (data: RegroupFormData) => void;
  onSave: () => void;
  onSubmit?: () => void;
  onClose: () => void;
  isProcessing?: boolean;
}

export const RegroupFormModal: React.FC<RegroupFormModalProps> = ({
  isOpen, mode, formData, onFormDataChange, onSave, onSubmit, onClose, isProcessing = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchError, setSearchError] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [validated, setValidated] = useState(false);

  const { data: customers } = useCustomers();
  const { data: goldRates } = useGoldRates();
  const { data: systemConfigs } = useSystemConfig();

  const activeRate = useMemo(() => goldRates.find((r) => r.isActive), [goldRates]);
  const ratePerGram = activeRate?.ratePerGram ?? 0;
  const rateBasis = activeRate?.rateBasis ?? 'NET';
  const roundingUnit = useMemo(() => {
    const cfg = systemConfigs.find((c) => c.configKey === 'rounding_unit');
    return cfg ? parseFloat(cfg.configValue) || 5000 : 5000;
  }, [systemConfigs]);

  const customerOptions = useMemo(() =>
    customers.map((c: any) => ({ value: c.id, label: `${c.customerId} - ${c.customerName}` })),
    [customers]);

  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setSearchError('');
      setSearchPerformed(mode === 'edit');
      setValidated(false);
    }
  }, [isOpen, mode]);

  const calc = useMemo(() => computeRegroup(formData, ratePerGram, roundingUnit), [formData, ratePerGram, roundingUnit]);
  const retained = formData.closingGls.filter((g) => !g.close);

  if (!isOpen) return null;

  const isSearchDone = mode === 'edit' || (searchPerformed && !!formData.mappingId);

  const handleSearch = async () => {
    const term = searchTerm.trim().replace(/\s/g, '');
    if (!term) { setSearchError('Enter an Add-On account or GL number to search.'); return; }
    setSearchLoading(true);
    setSearchError('');
    try {
      const res = await regroupService.search(term);
      const lot = res.data;
      if (!lot) { setSearchError('No active lot found. Verify the account or GL number.'); setSearchPerformed(true); return; }
      onFormDataChange({
        ...formData,
        mappingId: lot.mappingId,
        mappingRef: lot.mappingReference ?? '',
        customerName: lot.customerName ?? '',
        customerId: lot.customerId ?? '',
        addonCustomerId: lot.addonCustomerId,
        loanAccountNumber: lot.addonLoanAccountNumber ?? '',
        addonLoanType: lot.addonLoanType ?? '',
        addonLoanDate: lot.addonLoanDate ?? '',
        cutoffDate: lot.cutoffDate,
        cutoffEligible: lot.isEligible,
        cutoffReason: lot.blockedReason ?? '',
        addonOutstandingPrincipal: '',
        addonOutstandingInterest: '',
        requestedAmount: '',
        closingGls: (lot.gls ?? []).map((g) => ({
          mappedGlId: g.mappedGlId,
          glNumber: g.glNumber ?? '',
          netWeight: g.netWeight,
          allocationRatio: g.allocationRatio,
          close: false,
          outstandingPrincipal: '',
          outstandingInterest: '',
          tokenLossCharge: '',
        })),
        newGls: [],
      });
      setSearchPerformed(true);
    } catch {
      setSearchError('Failed to search. Please try again.');
    } finally {
      setSearchLoading(false);
    }
  };

  const setClosingField = (idx: number, patch: Partial<RegroupClosingGlRow>) => {
    onFormDataChange({ ...formData, closingGls: formData.closingGls.map((g, i) => (i === idx ? { ...g, ...patch } : g)) });
  };

  const addNewGl = () => {
    const today = new Date().toISOString().split('T')[0];
    onFormDataChange({ ...formData, newGls: [...formData.newGls, { customerId: formData.addonCustomerId, glNumber: '', netWeight: '', loanAmount: '', documentationCharge: '', loanDate: today }] });
  };
  const removeNewGl = (idx: number) => onFormDataChange({ ...formData, newGls: formData.newGls.filter((_, i) => i !== idx) });
  const setNewGlField = (idx: number, patch: Partial<RegroupNewGlRow>) =>
    onFormDataChange({ ...formData, newGls: formData.newGls.map((g, i) => (i === idx ? { ...g, ...patch } : g)) });

  const reqIndicator = (() => {
    if (calc.requested <= 0) return null;
    if (calc.requested <= calc.eligible) return { label: 'Within eligible amount', color: '#16a34a' };
    if (!calc.exceedsMaximum) return { label: 'Between eligible and maximum permissible', color: '#d97706' };
    return { label: 'Exceeds maximum permissible', color: '#dc2626' };
  })();

  const saveDisabled = isProcessing || !formData.cutoffEligible || calc.eligibilityBlocked || calc.exceedsMaximum || calc.docInvalid;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <h2 className="modal-title">{mode === 'create' ? 'New Regroup Transaction' : 'Edit Regroup Transaction'}</h2>
          <button className="modal-close" onClick={onClose} disabled={isProcessing}><IconSystem.x size={20} /></button>
        </div>

        <div className="modal-content" style={{ overflow: 'auto', flex: 1 }}>
          {/* Non-gross basis warning */}
          {rateBasis !== 'FULL' && (
            <div style={{ display: 'flex', gap: '0.5rem', padding: '0.75rem 1rem', marginBottom: '1rem', background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '0.375rem', color: '#92400e', fontSize: '0.78rem' }}>
              <IconSystem.warning size={16} style={{ flexShrink: 0 }} />
              <span>The active gold rate is on the <strong>Net</strong> basis. Regroup eligibility assumes the <strong>Gross</strong> basis (CR-002). Activate a Gross rate before submitting.</span>
            </div>
          )}

          {/* ===== Search ===== */}
          <h3 style={sectionHeaderStyle}>Existing Lot</h3>
          {mode === 'create' && !isSearchDone && (
            <div style={{ marginBottom: '1.5rem' }}>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>Search by the Add-On account number or a GL number to find the lot.</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '1rem' }}>
                <input className="form-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value.replace(/\s/g, ''))} placeholder="Account / GL number" style={{ border: '1px solid #334155' }} onKeyDown={(e) => e.key === 'Enter' && handleSearch()} />
                <button className="btn btn-primary" onClick={handleSearch} disabled={searchLoading} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                  <IconSystem.search size={16} />{searchLoading ? 'Searching...' : 'Search'}
                </button>
              </div>
              {searchError && (
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', padding: '0.625rem 0.875rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.375rem', color: '#dc2626', fontSize: '0.8rem' }}>
                  <IconSystem.alert size={14} />{searchError}
                </div>
              )}
            </div>
          )}

          {isSearchDone && (
            <div style={{ marginBottom: '1.25rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', background: 'var(--bg-secondary)', padding: '1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)' }}>
                <div><div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Mapping</div><div style={{ fontWeight: 600, fontFamily: 'monospace' }}>{formData.mappingRef}</div></div>
                <div><div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Customer</div><div style={{ fontWeight: 600 }}>{formData.customerName} ({formData.customerId})</div></div>
                <div><div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Add-On Account</div><div style={{ fontWeight: 600, fontFamily: 'monospace' }}>{formData.loanAccountNumber}</div></div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Add-On Disbursed</div>
                  <div style={{ fontWeight: 600 }}>
                    {formData.addonLoanDate ? formatDate(formData.addonLoanDate) : '-'}{' '}
                    {formData.cutoffEligible
                      ? <span style={{ fontSize: '0.62rem', fontWeight: 600, padding: '0.05rem 0.4rem', borderRadius: '0.625rem', background: '#dcfce7', color: '#15803d' }}>✓ after cut-off {formData.cutoffDate ? formatDate(formData.cutoffDate) : ''}</span>
                      : <span style={{ fontSize: '0.62rem', fontWeight: 600, padding: '0.05rem 0.4rem', borderRadius: '0.625rem', background: '#fee2e2', color: '#b91c1c' }}>blocked</span>}
                  </div>
                </div>
              </div>
              {mode === 'create' && (
                <button type="button" onClick={() => { setSearchPerformed(false); onFormDataChange({ ...formData, mappingId: '' }); }} style={{ marginTop: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', fontSize: '0.75rem', textDecoration: 'underline' }}>← Search Again</button>
              )}
            </div>
          )}

          {/* Cut-off blocked banner */}
          {isSearchDone && !formData.cutoffEligible && (
            <div style={{ display: 'flex', gap: '0.5rem', padding: '0.875rem 1rem', marginBottom: '1.25rem', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '0.5rem', color: '#991b1b', fontSize: '0.8rem' }}>
              <IconSystem.alert size={18} style={{ flexShrink: 0 }} />
              <div><div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>Lot not eligible for Regroup</div><div>{formData.cutoffReason}</div></div>
            </div>
          )}

          {isSearchDone && formData.cutoffEligible && (
            <>
              {/* ===== Closing leg ===== */}
              <h3 style={sectionHeaderStyle}>Closing Leg — Close Add-On Loan &amp; Selected GL(s)</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '0.75rem' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Add-On Outstanding Principal *</label>
                  <input type="number" min={0} step="0.01" className="form-input" value={formData.addonOutstandingPrincipal}
                    onChange={(e) => onFormDataChange({ ...formData, addonOutstandingPrincipal: e.target.value })}
                    style={{ border: `1px solid ${validated && !formData.addonOutstandingPrincipal ? '#dc2626' : '#334155'}` }} />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Add-On Outstanding Interest *</label>
                  <input type="number" min={0} step="0.01" className="form-input" value={formData.addonOutstandingInterest}
                    onChange={(e) => onFormDataChange({ ...formData, addonOutstandingInterest: e.target.value })} style={{ border: '1px solid #334155' }} />
                </div>
              </div>
              <div style={{ fontSize: '0.72rem', color: 'var(--danger)', fontWeight: 600, marginBottom: '0.75rem' }}>
                ⓘ The Add-On Loan is settled in full — principal + interest are collected entirely.
              </div>

              <div style={{ overflowX: 'auto', marginBottom: '1.25rem', border: '1px solid var(--border-color)', borderRadius: '0.375rem' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem' }}>
                  <thead><tr style={{ background: 'var(--bg-tertiary)' }}>
                    <th style={{ ...thBase, width: '150px' }}>Action</th><th style={thBase}>GL #</th>
                    <th style={thRight}>Weight (g)</th><th style={thRight}>Ratio</th>
                    <th style={thRight}>O/S Principal</th><th style={thRight}>O/S Interest</th>
                    <th style={thRight}>Token Loss</th><th style={thRight}>GL Settlement</th>
                  </tr></thead>
                  <tbody>
                    {formData.closingGls.map((gl, idx) => (
                      <tr key={gl.mappedGlId} style={{ background: gl.close ? 'rgba(239,68,68,0.05)' : 'rgba(20,184,166,0.05)' }}>
                        <td style={{ padding: '0.375rem' }}>
                          <label style={{ marginRight: '8px', cursor: 'pointer', fontWeight: 600, color: 'var(--danger)' }}>
                            <input type="radio" checked={gl.close} onChange={() => setClosingField(idx, { close: true })} /> Close
                          </label>
                          <label style={{ cursor: 'pointer', fontWeight: 600, color: '#0f766e' }}>
                            <input type="radio" checked={!gl.close} onChange={() => setClosingField(idx, { close: false, outstandingPrincipal: '', outstandingInterest: '', tokenLossCharge: '' })} /> Retain
                          </label>
                        </td>
                        <td style={{ padding: '0.375rem', fontFamily: 'monospace' }}>{gl.glNumber}</td>
                        <td style={{ padding: '0.375rem', textAlign: 'right' }}>{gl.netWeight.toFixed(3)}</td>
                        <td style={{ padding: '0.375rem', textAlign: 'right', fontWeight: 500 }}>{(gl.allocationRatio * 100).toFixed(2)}%</td>
                        <td style={{ padding: '0.375rem', textAlign: 'right' }}>
                          <input className="form-input" type="number" min="0" step="0.01" value={gl.outstandingPrincipal} disabled={!gl.close}
                            onChange={(e) => setClosingField(idx, { outstandingPrincipal: e.target.value })} style={cellInput} />
                        </td>
                        <td style={{ padding: '0.375rem', textAlign: 'right' }}>
                          <input className="form-input" type="number" min="0" step="0.01" value={gl.outstandingInterest} disabled={!gl.close}
                            onChange={(e) => setClosingField(idx, { outstandingInterest: e.target.value })} style={cellInput} />
                        </td>
                        <td style={{ padding: '0.375rem', textAlign: 'right' }}>
                          <input className="form-input" type="number" min="0" step="0.01" value={gl.tokenLossCharge} disabled={!gl.close} title="Optional — defaults to 0"
                            onChange={(e) => setClosingField(idx, { tokenLossCharge: e.target.value })} style={{ ...cellInput, width: '75px' }} />
                        </td>
                        <td style={{ padding: '0.375rem', textAlign: 'right', fontWeight: 600 }}>
                          {gl.close ? formatCurrency((parseFloat(gl.outstandingPrincipal) || 0) + (parseFloat(gl.outstandingInterest) || 0) + (parseFloat(gl.tokenLossCharge) || 0)) : <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>joins new lot</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Closing-leg summary */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem', padding: '0.875rem', marginBottom: '1.25rem', background: 'rgba(239,68,68,0.06)', borderRadius: '0.5rem', border: '1px solid var(--border-color)' }}>
                <div><div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Combined Ratio (closed GLs)</div><div style={{ fontWeight: 600 }}>{(calc.closedRatio * 100).toFixed(2)}%</div></div>
                <div><div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Closed GL Settlement (P+I+Token)</div><div style={{ fontWeight: 600 }}>{formatCurrency(calc.closedGlSettlement)}</div></div>
                <div><div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>of which Token Loss Charges</div><div style={{ fontWeight: 600 }}>{formatCurrency(calc.tokenLossTotal)}</div></div>
                <div><div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Add-On Settlement (full P+I)</div><div style={{ fontWeight: 600 }}>{formatCurrency(calc.addonSettlement)}</div></div>
                <div><div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Closed Principal Portion</div><div style={{ fontWeight: 600 }}>{formatCurrency(calc.closedPrincipalPortion)}</div></div>
                <div><div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Total Closing Leg</div><div style={{ fontWeight: 700, color: 'var(--danger)' }}>{formatCurrency(calc.closingLegTotal)}</div></div>
              </div>

              {/* ===== New lot leg ===== */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ ...sectionHeaderStyle, flex: 1 }}>New Lot Leg — New GL(s) + Fresh Add-On Loan</h3>
                <button type="button" onClick={addNewGl} className="btn btn-secondary" style={{ fontSize: '0.72rem', padding: '0.25rem 0.6rem' }}><IconSystem.add size={12} /> Add GL</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', margin: '0.5rem 0 0.75rem' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Customer</label>
                  <input className="form-input" value={formData.customerName ? `${formData.customerName}${formData.customerId ? ` (${formData.customerId})` : ''}` : ''} disabled title="The new Add-On Loan is created for the same customer as the lot being regrouped" />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">New Add-On Account Number * <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(14 characters)</span></label>
                  <input className="form-input" maxLength={14} value={formData.newAddonLoanAccountNumber}
                    onChange={(e) => onFormDataChange({ ...formData, newAddonLoanAccountNumber: e.target.value.replace(/\s/g, '') })}
                    placeholder="14-char account #" style={{ border: '1px solid #334155', fontFamily: 'monospace' }} />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Loan Type</label>
                  <input className="form-input" value={formData.addonLoanType} onChange={(e) => onFormDataChange({ ...formData, addonLoanType: e.target.value })} style={{ border: '1px solid #334155' }} />
                </div>
              </div>

              <div style={{ overflowX: 'auto', marginBottom: '1rem', border: '1px solid var(--border-color)', borderRadius: '0.375rem' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem' }}>
                  <thead><tr style={{ background: 'var(--bg-tertiary)' }}>
                    <th style={thBase}>Source</th><th style={thBase}>GL #</th><th style={thBase}>Customer</th>
                    <th style={thRight}>Weight (g)</th><th style={thRight}>Loan (= O/S)</th><th style={thRight}>Doc Charge</th>
                    <th style={thRight}>Disbursement</th><th style={thBase}>Loan Date</th><th style={thRight}>New Ratio</th><th style={thBase}></th>
                  </tr></thead>
                  <tbody>
                    {formData.newGls.map((gl, idx) => {
                      const w = parseFloat(gl.netWeight) || 0;
                      const ratio = calc.totalNewLotWeight > 0 ? (w / calc.totalNewLotWeight) * 100 : 0;
                      const disb = (parseFloat(gl.loanAmount) || 0) - (parseFloat(gl.documentationCharge) || 0);
                      const docBad = (parseFloat(gl.documentationCharge) || 0) > 0 && (parseFloat(gl.documentationCharge) || 0) >= (parseFloat(gl.loanAmount) || 0);
                      return (
                        <tr key={idx}>
                          <td style={{ padding: '0.375rem' }}><span style={{ fontSize: '0.65rem', fontWeight: 600, padding: '0.05rem 0.4rem', borderRadius: '0.625rem', background: '#dbeafe', color: '#1d4ed8' }}>New</span></td>
                          <td style={{ padding: '0.375rem' }}><input className="form-input" maxLength={14} value={gl.glNumber} onChange={(e) => setNewGlField(idx, { glNumber: e.target.value.replace(/\s/g, '') })} style={{ width: '130px', fontFamily: 'monospace', fontSize: '0.72rem', padding: '0.25rem 0.375rem', border: `1px solid ${validated && !gl.glNumber ? '#dc2626' : '#cbd5e1'}` }} /></td>
                          <td style={{ padding: '0.375rem', minWidth: '150px' }}>
                            <SyutSelect options={customerOptions} value={gl.customerId} onChange={(val) => setNewGlField(idx, { customerId: (val as string) || '' })} placeholder="Customer..." isSearchable isClearable size="sm" menuPortalTarget={document.body} />
                          </td>
                          <td style={{ padding: '0.375rem', textAlign: 'right' }}><input className="form-input" type="number" min="0" step="0.001" value={gl.netWeight} onChange={(e) => setNewGlField(idx, { netWeight: e.target.value })} style={cellInput} /></td>
                          <td style={{ padding: '0.375rem', textAlign: 'right' }}><input className="form-input" type="number" min="0" step="0.01" value={gl.loanAmount} onChange={(e) => setNewGlField(idx, { loanAmount: e.target.value })} style={cellInput} /></td>
                          <td style={{ padding: '0.375rem', textAlign: 'right' }}><input className="form-input" type="number" min="0" step="0.01" value={gl.documentationCharge} title="Optional — defaults to 0" onChange={(e) => setNewGlField(idx, { documentationCharge: e.target.value })} style={{ ...cellInput, width: '75px', borderColor: docBad ? '#dc2626' : undefined }} /></td>
                          <td style={{ padding: '0.375rem', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(disb)}</td>
                          <td style={{ padding: '0.375rem' }}><input className="form-input" type="date" value={gl.loanDate} onChange={(e) => setNewGlField(idx, { loanDate: e.target.value })} style={{ width: '130px', fontSize: '0.72rem', padding: '0.25rem 0.375rem' }} /></td>
                          <td style={{ padding: '0.375rem', textAlign: 'right', fontWeight: 600 }}>{ratio.toFixed(2)}%</td>
                          <td style={{ padding: '0.375rem' }}><button type="button" onClick={() => removeNewGl(idx)} style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer' }}><IconSystem.trash size={14} /></button></td>
                        </tr>
                      );
                    })}
                    {retained.map((gl) => {
                      const ratio = calc.totalNewLotWeight > 0 ? (gl.netWeight / calc.totalNewLotWeight) * 100 : 0;
                      return (
                        <tr key={gl.mappedGlId} style={{ background: 'rgba(20,184,166,0.05)' }}>
                          <td style={{ padding: '0.375rem' }}><span style={{ fontSize: '0.65rem', fontWeight: 600, padding: '0.05rem 0.4rem', borderRadius: '0.625rem', background: '#ccfbf1', color: '#0f766e' }}>Retained</span></td>
                          <td style={{ padding: '0.375rem', fontFamily: 'monospace' }}>{gl.glNumber}</td>
                          <td style={{ padding: '0.375rem', color: 'var(--text-muted)' }}>— existing GL —</td>
                          <td style={{ padding: '0.375rem', textAlign: 'right' }}>{gl.netWeight.toFixed(3)}</td>
                          <td style={{ padding: '0.375rem', textAlign: 'right', color: 'var(--text-muted)' }}>—</td>
                          <td style={{ padding: '0.375rem', textAlign: 'right', color: 'var(--text-muted)' }}>—</td>
                          <td style={{ padding: '0.375rem', textAlign: 'right', color: 'var(--text-muted)' }}>—</td>
                          <td style={{ padding: '0.375rem', color: 'var(--text-muted)' }}>from lot</td>
                          <td style={{ padding: '0.375rem', textAlign: 'right', fontWeight: 600 }}>{ratio.toFixed(2)}%</td>
                          <td />
                        </tr>
                      );
                    })}
                    {formData.newGls.length === 0 && retained.length === 0 && (
                      <tr><td colSpan={10} style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)' }}>No GLs in the new lot yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* ===== Eligibility panel ===== */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem', padding: '0.875rem', marginBottom: '0.5rem', background: 'rgba(34,197,94,0.06)', borderRadius: '0.5rem', border: '1px solid var(--border-color)' }}>
                <div><div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Gross Gold Value (new GLs)</div><div style={{ fontWeight: 600 }}>{calc.newWeight.toFixed(3)} g × {formatCurrency(ratePerGram)} = {formatCurrency(calc.newGlGross)}</div></div>
                <div><div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Less New GL Outstanding (full loan amount)</div><div style={{ fontWeight: 600 }}>{formatCurrency(calc.newGlLoan)}</div></div>
                <div><div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>New GL Eligibility</div><div style={{ fontWeight: 600, color: calc.newGlEligibility < 0 ? '#dc2626' : undefined }}>{formatCurrency(calc.newGlEligibility)}</div></div>
                <div><div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Carried Outstanding</div><div style={{ fontWeight: 600 }}>{formatCurrency(calc.carried)}</div></div>
                <div><div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Eligible Amount</div><div style={{ fontWeight: 700, color: calc.eligible > 0 ? '#16a34a' : '#dc2626' }}>{formatCurrency(calc.eligible)}</div></div>
                <div><div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Max Permissible</div><div style={{ fontWeight: 700, color: 'var(--primary)' }}>{formatCurrency(calc.maximumPermissible)}</div></div>
              </div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', background: 'var(--bg-secondary)', border: '1px dashed var(--border-color)', borderRadius: '0.375rem', padding: '0.5rem 0.75rem', marginBottom: '1rem' }}>
                Eligible = New GL ({formatCurrency(calc.newGlGross)} gross − {formatCurrency(calc.newGlLoan)} o/s = {formatCurrency(calc.newGlEligibility)}) + Add-On o/s principal {formatCurrency(parseFloat(formData.addonOutstandingPrincipal) || 0)} − Closed portion {formatCurrency(calc.closedPrincipalPortion)} ({(calc.closedRatio * 100).toFixed(0)}%) = {formatCurrency(calc.eligible)} → Max {formatCurrency(calc.maximumPermissible)}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Requested Amount *</label>
                  <input type="number" min={0} step="0.01" className="form-input" value={formData.requestedAmount}
                    onChange={(e) => onFormDataChange({ ...formData, requestedAmount: e.target.value })}
                    style={{ border: `1px solid ${reqIndicator ? reqIndicator.color : '#334155'}`, fontWeight: 700 }} />
                  {reqIndicator && <span style={{ fontSize: '0.7rem', fontWeight: 700, color: reqIndicator.color, display: 'block', marginTop: '0.25rem' }}>{reqIndicator.label}</span>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', paddingTop: '1.1rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                  Max:&nbsp;<b style={{ color: 'var(--primary)' }}>{formatCurrency(calc.maximumPermissible)}</b>
                </div>
              </div>

              {/* ===== Net cash flow ===== */}
              <div style={{ borderRadius: '0.5rem', padding: '1rem', textAlign: 'center', border: `2px solid ${calc.netCashFlow >= 0 ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`, background: calc.netCashFlow >= 0 ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)' }}>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>NET CASH FLOW</div>
                <div style={{ fontSize: '1.4rem', fontWeight: 700, color: calc.netCashFlow >= 0 ? '#16a34a' : '#dc2626' }}>{calc.netCashFlow >= 0 ? '+' : ''}{formatCurrency(calc.netCashFlow)}</div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>{calc.netCashFlow >= 0 ? 'Customer receives funds' : 'Customer must bring in shortfall'}</div>
                <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>(New Add-On {formatCurrency(calc.requested)} + New GL disbursement {formatCurrency(calc.newGlDisbursementTotal)} after {formatCurrency(calc.newGlLoan - calc.newGlDisbursementTotal)} doc charge) − Closing Leg {formatCurrency(calc.closingLegTotal)} (incl. {formatCurrency(calc.tokenLossTotal)} token loss)</div>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
          <div>
            {calc.exceedsMaximum && <span style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 600 }}>Requested exceeds maximum permissible ({formatCurrency(calc.maximumPermissible)})</span>}
            {!calc.exceedsMaximum && calc.docInvalid && <span style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 600 }}>Documentation charge must be less than the loan amount</span>}
            {!calc.exceedsMaximum && !calc.docInvalid && calc.eligibilityBlocked && isSearchDone && <span style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 600 }}>Eligible amount must be greater than zero</span>}
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button className="btn btn-secondary" onClick={onClose} disabled={isProcessing}>Cancel</button>
            <button className="btn btn-primary" onClick={() => { setValidated(true); onSave(); }} disabled={saveDisabled} style={saveDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}>
              <IconSystem.save size={16} />{mode === 'create' ? 'Save as Draft' : 'Save as Draft'}
            </button>
            {onSubmit && (
              <button className="btn btn-primary" onClick={() => { setValidated(true); onSubmit(); }} disabled={saveDisabled} style={{ background: '#16a34a', borderColor: '#16a34a', ...(saveDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}>
                <IconSystem.check size={16} />Submit
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// ============================================================================
// View Modal
// ============================================================================

interface RegroupViewModalProps {
  isOpen: boolean;
  record: RegroupRecord | null;
  onClose: () => void;
}

export const RegroupViewModal: React.FC<RegroupViewModalProps> = ({ isOpen, record, onClose }) => {
  const [detail, setDetail] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && record?.id) {
      setLoading(true);
      regroupService.getById(record.id)
        .then((res) => setDetail(res.data))
        .catch(() => setDetail(null))
        .finally(() => setLoading(false));
    } else {
      setDetail(null);
    }
  }, [isOpen, record?.id]);

  if (!isOpen || !record) return null;

  const closing = detail?.closingDetail;
  const newReq = detail?.newRequestDetail;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <h2 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            Regroup — <span style={{ fontFamily: 'monospace' }}>{record.regroupRef}</span>
            <span style={getStatusBadgeStyle(record.statusCode ?? '')}>{(record.statusCode ?? '').replace(/_/g, ' ')}</span>
          </h2>
          <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content" style={{ overflow: 'auto', flex: 1 }}>
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
          ) : (
            <>
              {/* Closing leg */}
              <div style={{ border: '1px solid var(--border-color)', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1rem' }}>
                <h3 style={{ ...sectionHeaderStyle, color: 'var(--danger)', borderColor: 'var(--danger)' }}>Closing Leg — {record.mappingReference}</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem' }}>
                  <div><div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Customer</div><div style={{ fontWeight: 600 }}>{record.customerName} ({record.customerId})</div></div>
                  <div><div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Add-On Account</div><div style={{ fontWeight: 600, fontFamily: 'monospace' }}>{closing?.loanAccountNumber ?? '-'}</div></div>
                  <div><div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Add-On Settlement (full P+I)</div><div style={{ fontWeight: 600 }}>{formatCurrency((closing?.addonOutstandingPrincipal ?? 0) + (closing?.addonOutstandingInterest ?? 0))}</div></div>
                  <div><div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Closed Principal Portion</div><div style={{ fontWeight: 600 }}>{formatCurrency(detail?.closedPrincipalPortion ?? 0)}</div></div>
                  <div><div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Total Closing Leg</div><div style={{ fontWeight: 700, color: 'var(--danger)' }}>{formatCurrency(detail?.closingLegTotal ?? 0)}</div></div>
                </div>
                {closing?.glEntries?.length > 0 && (
                  <div style={{ overflowX: 'auto', marginTop: '0.75rem', border: '1px solid var(--border-color)', borderRadius: '0.375rem' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem' }}>
                      <thead><tr style={{ background: 'var(--bg-tertiary)' }}>
                        <th style={thBase}>GL #</th><th style={thRight}>Weight (g)</th><th style={thRight}>Ratio</th>
                        <th style={thRight}>O/S Principal</th><th style={thRight}>O/S Interest</th><th style={thRight}>Token Loss</th><th style={thRight}>GL Settlement</th>
                      </tr></thead>
                      <tbody>
                        {closing.glEntries.map((e: any, i: number) => (
                          <tr key={i}>
                            <td style={{ padding: '0.375rem', fontFamily: 'monospace' }}>{e.glNumber}</td>
                            <td style={{ padding: '0.375rem', textAlign: 'right' }}>{(e.netWeight ?? 0).toFixed(3)}</td>
                            <td style={{ padding: '0.375rem', textAlign: 'right' }}>{((e.allocationRatio ?? 0) * 100).toFixed(2)}%</td>
                            <td style={{ padding: '0.375rem', textAlign: 'right' }}>{formatCurrency(e.outstandingPrincipal ?? 0)}</td>
                            <td style={{ padding: '0.375rem', textAlign: 'right' }}>{formatCurrency(e.outstandingInterest ?? 0)}</td>
                            <td style={{ padding: '0.375rem', textAlign: 'right' }}>{formatCurrency(e.tokenLossCharge ?? 0)}</td>
                            <td style={{ padding: '0.375rem', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(e.glSettlement ?? ((e.outstandingPrincipal ?? 0) + (e.outstandingInterest ?? 0) + (e.tokenLossCharge ?? 0)))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* New lot leg */}
              <div style={{ border: '1px solid var(--border-color)', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1rem' }}>
                <h3 style={{ ...sectionHeaderStyle, color: 'var(--success)', borderColor: 'var(--success)' }}>New Lot Leg — {newReq?.addonLoanAccountNumber ?? '-'}</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem' }}>
                  <div><div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Gross Gold Value (new GLs)</div><div style={{ fontWeight: 600 }}>{formatCurrency(newReq?.grossValue ?? 0)}</div></div>
                  <div><div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Less New GL Outstanding</div><div style={{ fontWeight: 600 }}>{formatCurrency((newReq?.grossValue ?? 0) - (detail?.newGlEligibility ?? 0))}</div></div>
                  <div><div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>New GL Eligibility</div><div style={{ fontWeight: 600 }}>{formatCurrency(detail?.newGlEligibility ?? 0)}</div></div>
                  <div><div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Carried Outstanding</div><div style={{ fontWeight: 600 }}>{formatCurrency(detail?.carriedOutstanding ?? 0)}</div></div>
                  <div><div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Eligible Amount</div><div style={{ fontWeight: 600 }}>{formatCurrency(detail?.eligibleAmount ?? 0)}</div></div>
                  <div><div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Max Permissible</div><div style={{ fontWeight: 600 }}>{formatCurrency(detail?.maximumPermissible ?? 0)}</div></div>
                  <div><div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Requested / Approved</div><div style={{ fontWeight: 600, color: 'var(--success)' }}>{formatCurrency(newReq?.requestedAmount ?? record.newAddonAmount ?? 0)}</div></div>
                </div>
                {newReq?.glEntries?.length > 0 && (() => {
                  const totalW = newReq.glEntries.reduce((s: number, g: any) => s + (g.netWeight ?? 0), 0);
                  return (
                    <div style={{ overflowX: 'auto', marginTop: '0.75rem', border: '1px solid var(--border-color)', borderRadius: '0.375rem' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem' }}>
                        <thead><tr style={{ background: 'var(--bg-tertiary)' }}>
                          <th style={thBase}>Source</th><th style={thBase}>GL #</th><th style={thRight}>Weight (g)</th>
                          <th style={thRight}>Loan Amount</th><th style={thRight}>Doc Charge</th><th style={thRight}>Disbursement</th><th style={thRight}>New Ratio</th>
                        </tr></thead>
                        <tbody>
                          {newReq.glEntries.map((g: any, i: number) => {
                            const retained = !!g.sourceMappedGlId;
                            const ratio = totalW > 0 ? ((g.netWeight ?? 0) / totalW) * 100 : 0;
                            return (
                              <tr key={i} style={retained ? { background: 'rgba(20,184,166,0.05)' } : undefined}>
                                <td style={{ padding: '0.375rem' }}><span style={{ fontSize: '0.62rem', fontWeight: 600, padding: '0.05rem 0.4rem', borderRadius: '0.625rem', background: retained ? '#ccfbf1' : '#dbeafe', color: retained ? '#0f766e' : '#1d4ed8' }}>{retained ? 'Retained' : 'New'}</span></td>
                                <td style={{ padding: '0.375rem', fontFamily: 'monospace' }}>{g.glNumber}</td>
                                <td style={{ padding: '0.375rem', textAlign: 'right' }}>{(g.netWeight ?? 0).toFixed(3)}</td>
                                <td style={{ padding: '0.375rem', textAlign: 'right' }}>{retained ? '—' : formatCurrency(g.originalLoanAmount ?? 0)}</td>
                                <td style={{ padding: '0.375rem', textAlign: 'right' }}>{retained ? '—' : formatCurrency(g.documentationCharge ?? 0)}</td>
                                <td style={{ padding: '0.375rem', textAlign: 'right', fontWeight: 600 }}>{retained ? '—' : formatCurrency(g.disbursementAmount ?? ((g.originalLoanAmount ?? 0) - (g.documentationCharge ?? 0)))}</td>
                                <td style={{ padding: '0.375rem', textAlign: 'right', fontWeight: 600 }}>{ratio.toFixed(2)}%</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </div>

              {/* Net cash flow */}
              <div style={{ borderRadius: '0.5rem', padding: '1rem', textAlign: 'center', border: `2px solid ${record.netCashFlow >= 0 ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`, background: record.netCashFlow >= 0 ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)' }}>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>NET CASH FLOW</div>
                <div style={{ fontSize: '1.4rem', fontWeight: 700, color: record.netCashFlow >= 0 ? '#16a34a' : '#dc2626' }}>{record.netCashFlow >= 0 ? '+' : ''}{formatCurrency(record.netCashFlow)}</div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{record.netCashFlow >= 0 ? 'Customer receives funds' : 'Customer must bring in shortfall'}</div>
              </div>

              <div style={{ marginTop: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem', fontSize: '0.8rem' }}>
                <div><div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Submitted By</div><div>{record.submittedByName ?? '-'}</div></div>
                <div><div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Submitted At</div><div>{record.submittedAt ? formatDate(record.submittedAt) : '-'}</div></div>
                <div><div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Created By</div><div>{record.createdByName ?? '-'}</div></div>
                <div><div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Gold Rate Applied</div><div>{newReq?.ratePerGram ? `${formatCurrency(newReq.ratePerGram)}/g — ${newReq.rateBasis === 'FULL' ? 'Gross' : 'Net'} basis` : '-'}</div></div>
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </>
  );
};
